import type { GroupId, UserId, ViewModelAssetId, ViewModelId } from 'balus-common';
import { RTDBPath, ViewModelFileStorageLimitBytes } from 'balus-common';
import { TRPCClientError } from '@trpc/client';
import { t } from 'i18next';
import { trpcClient } from '@framework/trpcClient';
import { ObjectRepository } from '@framework/repository';
import type { FileContainerAssetCopy } from '@view-model/models/sticky/FileContainer/domain/FileContainer';
import { ViewModelEntity } from './ViewModelEntity';

type CopyTarget = { groupId: GroupId; viewModelId: ViewModelId };

type FileContainerAssetCloneResult =
  { ok: true; assetCopy: FileContainerAssetCopy } | { ok: false; errorMessage: string };

export class FileContainerAssetCloner {
  static async clone(
    sourceViewModelId: ViewModelId,
    target: CopyTarget,
    assetIds: ViewModelAssetId[],
    currentUserId: UserId
  ): Promise<FileContainerAssetCloneResult> {
    if (assetIds.length === 0) {
      return { ok: true, assetCopy: { assetIdMap: {}, createdUserId: currentUserId } };
    }

    try {
      const sourceGroupId = await this.groupIdOf(sourceViewModelId, target);
      const assetIdMap = await trpcClient.viewModel.copyFileAssets.mutate({
        sourceGroupId,
        sourceViewModelId,
        targetGroupId: target.groupId,
        targetViewModelId: target.viewModelId,
        assetIds,
      });
      return { ok: true, assetCopy: { assetIdMap, createdUserId: currentUserId } };
    } catch (error) {
      console.error('ファイルを複製できませんでした', error);
      return { ok: false, errorMessage: this.errorMessageOf(error) };
    }
  }

  private static async groupIdOf(viewModelId: ViewModelId, target: CopyTarget): Promise<GroupId> {
    if (viewModelId === target.viewModelId) return target.groupId;

    const entity = await new ObjectRepository(ViewModelEntity, RTDBPath.ViewModel.viewModelPath(viewModelId)).get();
    if (!entity) throw new Error(`ViewModel(id=${viewModelId}) is not found.`);
    return entity.groupId;
  }

  private static errorMessageOf(error: unknown): string {
    const code = error instanceof TRPCClientError ? error.shape?.data?.code : undefined;
    if (code === 'PAYLOAD_TOO_LARGE') {
      return t('canvas.fileContainer.duplicate.overLimit', { limitGB: ViewModelFileStorageLimitBytes / 1024 ** 3 });
    }
    if (code === 'FORBIDDEN') return t('canvas.fileContainer.duplicate.licenseRequired');
    // サーバーは URL 共有の匿名ユーザーを通常ユーザーではないとして BAD_REQUEST で拒否する
    if (code === 'BAD_REQUEST') return t('canvas.fileContainer.signInRequired');
    return t('canvas.fileContainer.duplicate.failed');
  }
}
